/**
 * Profile storage layer — localStorage now, D1 sync later.
 *
 * Keys:
 *   sparrowForge.bowProfiles.v1  → JSON array of BowProfile
 *   sparrowForge.arrowProfiles.v1 → JSON array of ArrowProfile
 *
 * Invariants enforced at write time:
 *   - Exactly one bow profile has isPrimary=true (or zero if no profiles)
 *   - Exactly one arrow profile has isActive=true (or zero if no profiles)
 *
 * If you set a bow primary, the previous primary is automatically cleared.
 * Same pattern for active arrow.
 */

import type { BowProfile, ArrowProfile } from './types';
import { mirrorToParent, requestParentValues } from './parentStorage';

/**
 * Storage keys. Both start with 'sparrow' so the storefront kv bridge
 * whitelists them (incident 4c — durable first-party mirror).
 */
export const BOW_KEY = 'sparrowForge.bowProfiles.v1';
export const ARROW_KEY = 'sparrowForge.arrowProfiles.v1';

function safeParse<T>(s: string | null, fallback: T): T {
  if (!s) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

/* ---------- Bow profiles ---------- */

export function loadBowProfiles(): BowProfile[] {
  if (typeof window === 'undefined') return [];
  return safeParse<BowProfile[]>(window.localStorage.getItem(BOW_KEY), []);
}

export function saveBowProfiles(profiles: BowProfile[]): void {
  if (typeof window === 'undefined') return;
  /* Enforce: at most one primary */
  const primaries = profiles.filter((p) => p.isPrimary);
  if (primaries.length > 1) {
    /* Keep the most recently updated primary */
    primaries.sort((a, b) => b.updatedAt - a.updatedAt);
    profiles = profiles.map((p) =>
      p.isPrimary && p.profileId !== primaries[0].profileId ? { ...p, isPrimary: false } : p
    );
  }
  const json = JSON.stringify(profiles);
  // incident 4c: ALWAYS mirror to the storefront's first-party storage. The
  // local write below lives on the forge.* iframe origin, which browsers may
  // wipe (Safari / iOS web app). The mirror is what survives.
  mirrorToParent(BOW_KEY, json);
  window.localStorage.setItem(BOW_KEY, json);
}

export function upsertBowProfile(profile: BowProfile): BowProfile[] {
  const profiles = loadBowProfiles();
  const idx = profiles.findIndex((p) => p.profileId === profile.profileId);
  let updated: BowProfile[];
  if (idx >= 0) {
    updated = [...profiles];
    updated[idx] = profile;
  } else {
    updated = [...profiles, profile];
  }
  /* If this profile is primary, unset primary on all others */
  if (profile.isPrimary) {
    updated = updated.map((p) =>
      p.profileId === profile.profileId ? p : { ...p, isPrimary: false }
    );
  }
  /* If no profile is primary, mark the first one (if any) as primary */
  if (!updated.some((p) => p.isPrimary) && updated.length > 0) {
    updated = updated.map((p, i) => (i === 0 ? { ...p, isPrimary: true } : p));
  }
  saveBowProfiles(updated);
  return updated;
}

export function deleteBowProfile(profileId: string): BowProfile[] {
  let profiles = loadBowProfiles();
  const wasPrimary = profiles.find((p) => p.profileId === profileId)?.isPrimary ?? false;
  profiles = profiles.filter((p) => p.profileId !== profileId);
  if (wasPrimary && profiles.length > 0) {
    profiles = profiles.map((p, i) => (i === 0 ? { ...p, isPrimary: true } : p));
  }
  saveBowProfiles(profiles);
  return profiles;
}

export function getPrimaryBow(): BowProfile | undefined {
  return loadBowProfiles().find((p) => p.isPrimary);
}

/* ---------- Arrow profiles ---------- */

export function loadArrowProfiles(): ArrowProfile[] {
  if (typeof window === 'undefined') return [];
  return safeParse<ArrowProfile[]>(window.localStorage.getItem(ARROW_KEY), []);
}

export function saveArrowProfiles(profiles: ArrowProfile[]): void {
  if (typeof window === 'undefined') return;
  const actives = profiles.filter((p) => p.isActive);
  if (actives.length > 1) {
    actives.sort((a, b) => b.updatedAt - a.updatedAt);
    profiles = profiles.map((p) =>
      p.isActive && p.profileId !== actives[0].profileId ? { ...p, isActive: false } : p
    );
  }
  const json = JSON.stringify(profiles);
  // incident 4c: mirror to durable first-party storage (see saveBowProfiles).
  mirrorToParent(ARROW_KEY, json);
  window.localStorage.setItem(ARROW_KEY, json);
}

export function upsertArrowProfile(profile: ArrowProfile): ArrowProfile[] {
  const profiles = loadArrowProfiles();
  const idx = profiles.findIndex((p) => p.profileId === profile.profileId);
  let updated: ArrowProfile[];
  if (idx >= 0) {
    updated = [...profiles];
    updated[idx] = profile;
  } else {
    updated = [...profiles, profile];
  }
  if (profile.isActive) {
    updated = updated.map((p) =>
      p.profileId === profile.profileId ? p : { ...p, isActive: false }
    );
  }
  if (!updated.some((p) => p.isActive) && updated.length > 0) {
    updated = updated.map((p, i) => (i === 0 ? { ...p, isActive: true } : p));
  }
  saveArrowProfiles(updated);
  return updated;
}

export function deleteArrowProfile(profileId: string): ArrowProfile[] {
  let profiles = loadArrowProfiles();
  const wasActive = profiles.find((p) => p.profileId === profileId)?.isActive ?? false;
  profiles = profiles.filter((p) => p.profileId !== profileId);
  if (wasActive && profiles.length > 0) {
    profiles = profiles.map((p, i) => (i === 0 ? { ...p, isActive: true } : p));
  }
  saveArrowProfiles(profiles);
  return profiles;
}

export function getActiveArrow(): ArrowProfile | undefined {
  return loadArrowProfiles().find((p) => p.isActive);
}

/* ---------- Parent-mirror hydration (incident 4c) ---------- */

/**
 * Union two profile lists by profileId, newest `updatedAt` wins on collision.
 * Durability-first: a profile present in only one side is KEPT (this is how a
 * wiped local store is restored from the parent mirror). Deletions propagate
 * because delete* re-saves the reduced list, which re-mirrors immediately —
 * so the parent never holds a profile the user has actually removed.
 */
function mergeById<T extends { profileId: string; updatedAt: number }>(
  local: T[],
  remote: T[],
): T[] {
  const byId = new Map<string, T>();
  for (const p of local) byId.set(p.profileId, p);
  for (const p of remote) {
    const existing = byId.get(p.profileId);
    if (!existing || p.updatedAt > existing.updatedAt) byId.set(p.profileId, p);
  }
  return [...byId.values()];
}

/** Enforce exactly one flagged profile (primary/active): newest wins. */
function normalizeFlag<T extends { profileId: string; updatedAt: number }>(
  profiles: T[],
  flag: keyof T,
): T[] {
  const flagged = profiles.filter((p) => p[flag]);
  if (flagged.length > 1) {
    flagged.sort((a, b) => b.updatedAt - a.updatedAt);
    const keep = flagged[0].profileId;
    return profiles.map((p) =>
      p[flag] && p.profileId !== keep ? { ...p, [flag]: false } : p,
    );
  }
  if (flagged.length === 0 && profiles.length > 0) {
    return profiles.map((p, i) => (i === 0 ? { ...p, [flag]: true } : p));
  }
  return profiles;
}

export function mergeBowProfiles(local: BowProfile[], remote: BowProfile[]): BowProfile[] {
  return normalizeFlag(mergeById(local, remote), 'isPrimary');
}

export function mergeArrowProfiles(local: ArrowProfile[], remote: ArrowProfile[]): ArrowProfile[] {
  return normalizeFlag(mergeById(local, remote), 'isActive');
}

/**
 * Boot-time hydration: pull the durable parent mirror and merge it into local
 * profile storage. This is what restores a customer's bows/arrows after the
 * iframe's own (third-party) storage has been evicted. Fail-open: if there is
 * no parent bridge the read times out to {} and nothing changes.
 *
 * Returns which lists actually changed so callers can refresh reactive state.
 */
export async function hydrateProfilesFromParent(): Promise<{
  bowsChanged: boolean;
  arrowsChanged: boolean;
}> {
  const result = { bowsChanged: false, arrowsChanged: false };
  if (typeof window === 'undefined') return result;

  const vals = await requestParentValues([BOW_KEY, ARROW_KEY]);

  const rawBows = vals[BOW_KEY];
  if (rawBows) {
    const remote = safeParse<BowProfile[]>(rawBows, []);
    if (Array.isArray(remote) && remote.length > 0) {
      const local = loadBowProfiles();
      const merged = mergeBowProfiles(local, remote);
      if (JSON.stringify(merged) !== JSON.stringify(local)) {
        saveBowProfiles(merged);
        result.bowsChanged = true;
      }
    }
  }

  const rawArrows = vals[ARROW_KEY];
  if (rawArrows) {
    const remote = safeParse<ArrowProfile[]>(rawArrows, []);
    if (Array.isArray(remote) && remote.length > 0) {
      const local = loadArrowProfiles();
      const merged = mergeArrowProfiles(local, remote);
      if (JSON.stringify(merged) !== JSON.stringify(local)) {
        saveArrowProfiles(merged);
        result.arrowsChanged = true;
      }
    }
  }

  return result;
}

/* ---------- UUID helper ---------- */

export function newProfileId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  /* Fallback for older environments */
  return 'p_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}
